import { useState, useEffect, useRef, useCallback } from "react";
import { WS_URL, ORG_CODE, WELCOME_HEADING } from "../config";

export const STATUS = {
  CONNECTING: "connecting",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  ERROR: "error",
};

export function useWebSocket() {
  const [status, setStatus] = useState(STATUS.DISCONNECTED);
  const [messages, setMessages] = useState([]);
  const [chat_widget_icon, setchat_widget_icon] = useState("");
  const [data_assistant_icon, setdata_assistant_icon] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const wsRef = useRef(null);
  const roomIdRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pendingMessageRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const welcomeTextRef = useRef("");
  // True while waiting on the reply to the hidden intro query — that
  // reply is discarded entirely (no chat bubble, no suggestion chips).
  const awaitingIntroRef = useRef(false);
  const MAX_RECONNECT = 5;

  const addMessage = useCallback((role, content, videoResult = null) => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        role,
        content,
        timestamp: new Date(),
        videoResult,
      },
    ]);
  }, []);

  const sendInitialQuery = useCallback((ws, chatRoomId) => {
    awaitingIntroRef.current = true;
    ws.send(
      JSON.stringify({
        type: "chat",
        message: "Who are you? and how you can help me? also add followup questions",
        chatroom_id: chatRoomId,
      }),
    );
  }, []);

  const connect = useCallback(() => {
    if (!WS_URL) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    setStatus(STATUS.CONNECTING);
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "init", org_code: ORG_CODE }));
    };

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === "connected") {
        roomIdRef.current = data.roomId;
        reconnectAttemptsRef.current = 0;
        setStatus(STATUS.CONNECTED);
        setchat_widget_icon(data.chat_widget_icon);
        setdata_assistant_icon(data["data-assistant-icon"]);

        // Dynamically update browser tab favicon from org's icon
        const iconUrl = data.chat_widget_icon || data["data-assistant-icon"];
        if (iconUrl) {
          ["icon", "shortcut icon", "apple-touch-icon"].forEach((rel) => {
            let link = document.querySelector(`link[rel='${rel}']`);
            if (!link) {
              link = document.createElement("link");
              link.rel = rel;
              document.head.appendChild(link);
            }
            link.href = iconUrl;
          });
        }
        // Seed the conversation with the welcome message as a real chat
        // bubble, and fetch real suggestions via a hidden intro query —
        // only on first connect, not on reconnects mid-session.
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true;
          welcomeTextRef.current = data.welcomeMessage || WELCOME_HEADING;
          setMessages([
            {
              id: Date.now() + Math.random(),
              role: "assistant",
              content: welcomeTextRef.current,
              followUpQuestions: null,
              timestamp: new Date(),
            },
          ]);
          sendInitialQuery(ws, data.roomId);
        }
        // Send any message queued before connection was ready
        if (pendingMessageRef.current) {
          const { text } = pendingMessageRef.current;
          pendingMessageRef.current = null;
          ws.send(
            JSON.stringify({
              type: "chat",
              message: text,
              chatroom_id: data.roomId,
            }),
          );
        }
        return;
      }

      if (data.type === "token") {
        if (awaitingIntroRef.current) return;
        setIsTyping(false);
        const content = data.message || data.text || data.content || "";
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === "assistant") {
            return [
              ...prev.slice(0, -1),
              { ...last, content: last.content + content },
            ];
          }
          return [
            ...prev,
            {
              id: Date.now() + Math.random(),
              role: "assistant",
              content,
              timestamp: new Date(),
            },
          ];
        });
        return;
      }

      if (data.type === "chat_response") {
        if (awaitingIntroRef.current) {
          // Hidden intro reply: discard it entirely (no suggestion chips
          // on the welcome message) — just clear the guard so normal
          // messages aren't suppressed afterwards.
          awaitingIntroRef.current = false;
          return;
        }
        setIsTyping(false);
        const content = data.message || data.text || data.content || "";
        const videoResult =
          data.video_result?.status && data.video_result?.data?.length > 0
            ? data.video_result.data
            : null;
        const followUpQuestions =
          Array.isArray(data.follow_up_questions) &&
          data.follow_up_questions.length > 0
            ? data.follow_up_questions
            : null;
        const sourceFileData =
          Array.isArray(data.source_file_data) && data.source_file_data.length > 0
            ? data.source_file_data
            : null;
        const imageResult =
          Array.isArray(data.image_result) && data.image_result.length > 0
            ? data.image_result
            : null;
        const formResult =
          Array.isArray(data.form_result) && data.form_result.length > 0
            ? data.form_result
            : null;
        const ownerId = data.owner_id || null;
        if (content) {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.role === "assistant") {
              return [
                ...prev.slice(0, -1),
                { ...last, content, videoResult, followUpQuestions, sourceFileData, imageResult, formResult, ownerId },
              ];
            }
            return [
              ...prev,
              {
                id: Date.now() + Math.random(),
                role: "assistant",
                content,
                videoResult,
                followUpQuestions,
                sourceFileData,
                imageResult,
                formResult,
                ownerId,
                timestamp: new Date(),
              },
            ];
          });
        }
        return;
      }

      if (data.type === "error") {
        setIsTyping(false);
        addMessage(
          "assistant",
          data.message || "An error occurred. Please try again.",
        );
        return;
      }
    };

    ws.onerror = () => {
      setStatus(STATUS.ERROR);
      setIsTyping(false);
    };

    ws.onclose = () => {
      setStatus(STATUS.DISCONNECTED);
      setIsTyping(false);
      roomIdRef.current = null;

      if (reconnectAttemptsRef.current < MAX_RECONNECT) {
        const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000);
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    };
  }, [addMessage, sendInitialQuery]);

  const sendMessage = useCallback(
    (text) => {
      if (!text.trim()) return false;

      addMessage("user", text);
      setIsTyping(true);

      const ws = wsRef.current;
      const isOpen = ws && ws.readyState === WebSocket.OPEN;
      const hasRoom = !!roomIdRef.current;

      if (isOpen && hasRoom) {
        ws.send(
          JSON.stringify({
            type: "chat",
            message: text,
            chatroom_id: roomIdRef.current,
          }),
        );
      } else {
        pendingMessageRef.current = { text };
        connect();
      }

      return true;
    },
    [addMessage, connect],
  );

  const clearMessages = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && roomIdRef.current) {
      setMessages([
        {
          id: Date.now() + Math.random(),
          role: "assistant",
          content: welcomeTextRef.current || WELCOME_HEADING,
          followUpQuestions: null,
          timestamp: new Date(),
        },
      ]);
      sendInitialQuery(ws, roomIdRef.current);
    } else {
      setMessages([]);
    }
  }, [sendInitialQuery]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectAttemptsRef.current = MAX_RECONNECT;
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  return {
    status,
    messages,
    isTyping,
    sendMessage,
    clearMessages,
    connect,
    STATUS,
    chat_widget_icon,
    data_assistant_icon,
  };
}
